import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Search, Settings2, X, Send, FileText, ChevronRight, AlertTriangle,
  CheckSquare, Square, Trash2, RotateCcw, SlidersHorizontal, ChevronDown,
} from "lucide-react";
import { api, API } from "../../lib/api";
import { canResendWelcome, welcomeTitle } from "../../lib/leadWelcome";
import { isTeam } from "../../lib/userRoles";
import { LEAD_PARAM } from "../../lib/leadLink";
import ExportButton from "./ExportButton";
import { downloadBlob } from "../../lib/downloadBlob";
import { exportName, exportPath } from "../../lib/exportFile";
import LeadDrawer from "./LeadDrawer";
import CountyCombobox from "../forms/CountyCombobox";
import { ROMANIAN_COUNTIES } from "../../lib/counties";
import Pagination from "./Pagination";
import { ENROLLMENT_FILTERS, matchBadge, matchTicks } from "../../lib/leadEnrollment";
import { OUTCOME_BY_ID, nextCallLabel, todayRo } from "../../lib/leadCalls";
import { leadSourceLabel } from "../../lib/leadSources";
import LeadCallPanel from "./LeadCallPanel";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";

// M916 — Server-side pagination default. 25 keeps the table to ~1.5 screens
// at the laptop breakpoint while still surfacing enough rows for context.
const DEFAULT_PAGE_SIZE = 25;

export const ALL_COLUMNS = [
  { key: "prenume", label: "Prenume", default: false },
  { key: "nume", label: "Nume", default: true },
  { key: "email", label: "Email", default: true },
  { key: "telefon", label: "Telefon", default: false },
  { key: "specializare", label: "Specializare", default: false },
  { key: "unitate", label: "Unitate", default: false },
  { key: "city", label: "Localitate", default: false },
  { key: "county", label: "Județ", default: true },
  { key: "status", label: "Status", default: true },
  // Where the person stands among enrollments, matched by email (phone as a
  // weaker hint). Replaces the old "Convertit?" yes/no, which only knew about
  // leads converted from the admin.
  { key: "enrollment", label: "Înscriere", default: true },
  // Last call and when the next one is due; the cell also records a call.
  { key: "last_call", label: "Apeluri", default: true },
  { key: "source", label: "Sursă", default: false },
  { key: "friendly_source", label: "Canal", default: true },
  { key: "consent_marketing", label: "Marketing", default: false },
  { key: "assigned_to_email", label: "Atribuit lui", default: true },
  { key: "form_variant", label: "Formular", default: false },
  { key: "popup_trigger_type", label: "Popup trigger", default: false },
  { key: "popup_cta_clicked", label: "Popup CTA?", default: false },
  { key: "utm_source", label: "UTM source", default: false },
  { key: "utm_medium", label: "UTM medium", default: false },
  { key: "utm_campaign", label: "UTM campaign", default: false },
  { key: "utm_content", label: "UTM content", default: false },
  { key: "utm_term", label: "UTM term", default: false },
  { key: "consent_privacy", label: "Privacy", default: false },
  { key: "created_at", label: "Data înscrierii", default: true },
  { key: "updated_at", label: "Ultima actualizare", default: false },
  { key: "notes_count", label: "Note", default: false },
];

const STATUSES = [
  { v: "lead_nou", label: "Nou", cls: "bg-[#3F8EBA]/15 text-[#1f5d80]" },
  { v: "contactat", label: "Contactat", cls: "bg-[#965EA7]/15 text-[#7A4986]" },
  { v: "interesat", label: "Interesat", cls: "bg-[#519872]/15 text-[#2F6B4E]" },
  { v: "convertit", label: "Convertit", cls: "bg-[#16a34a]/20 text-[#065F46]" },
  { v: "pierdut", label: "Pierdut", cls: "bg-[#E63946]/15 text-[#9b1f2c]" },
  { v: "duplicat", label: "Duplicat", cls: "bg-[#6B7280]/15 text-[#6B7280]" },
];
const STATUS_BY_VALUE = STATUSES.reduce((m, s) => { m[s.v] = s; return m; }, {});

// Only the sources the server writes; the filter matches them exactly.
const SOURCES = ["homepage_form", "popup", "webinar", "inscriere_neplatita"]
  .map((v) => ({ v, label: leadSourceLabel(v) }));

const FRIENDLY_SOURCES = [
  "Facebook", "Instagram", "Google Ads", "Organic",
  "LinkedIn", "YouTube", "TikTok", "Twitter",
  "Logiscool", "Trainer", "Email", "Direct", "Unknown",
];

const FRIENDLY_SOURCE_TONE = {
  Facebook:   "bg-[#1877F2]/12 text-[#1265CC]",
  Instagram:  "bg-[#E4405F]/12 text-[#C72B49]",
  "Google Ads": "bg-[#34A853]/12 text-[#1E7836]",
  Organic:    "bg-[#34A853]/12 text-[#1E7836]",
  LinkedIn:   "bg-[#0A66C2]/12 text-[#074E94]",
  YouTube:    "bg-[#FF0000]/10 text-[#8C0000]",
  TikTok:     "bg-[#373A45]/12 text-[#373A45]",
  Twitter:    "bg-[#373A45]/12 text-[#373A45]",
  Logiscool:  "bg-[#F5821F]/12 text-[#B45C10]",
  Trainer:    "bg-[#965EA7]/[0.12] text-[#7A4986]",
  Email:      "bg-[#F59E0B]/15 text-[#92400E]",
  Direct:     "bg-[#6B7280]/15 text-[#373A45]",
  Unknown:    "bg-[#6B7280]/10 text-[#6B7280]",
};

// Quick chips sit above the main filter row.
const CHIPS = [
  { id: "all", label: "Toate", filter: { status: "", date: "" } },
  { id: "today", label: "Azi", filter: { status: "", date: "today" } },
  { id: "last7", label: "Ultimele 7 zile", filter: { status: "", date: "last7" } },
  { id: "lead_nou", label: "Noi", filter: { status: "lead_nou", date: "" } },
  { id: "contactat", label: "Contactați", filter: { status: "contactat", date: "" } },
  { id: "interesat", label: "Interesați", filter: { status: "interesat", date: "" } },
  { id: "convertit", label: "Convertiți", filter: { status: "convertit", date: "" } },
  { id: "pierdut", label: "Pierduți", filter: { status: "pierdut", date: "" } },
];

function dateRangeToISO(range) {
  if (!range) return null;
  const now = new Date();
  if (range === "today") {
    const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    return start.toISOString();
  }
  if (range === "last7") {
    const start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    return start.toISOString();
  }
  return null;
}

function lsKey(email) { return `leads_columns:${email || "anon"}`; }
function loadColumns(email) {
  try {
    const raw = localStorage.getItem(lsKey(email));
    if (!raw) return null;
    let cols = JSON.parse(raw);
    if (!Array.isArray(cols) || !cols.length) return null;
    // Saved before a default column existed: add it once, after the one it
    // follows. Removing it afterwards is respected.
    for (const [key, after] of [["enrollment", "status"], ["last_call", "enrollment"]]) {
      const marker = `${lsKey(email)}:${key}-added`;
      if (cols.includes(key) || localStorage.getItem(marker)) continue;
      const old = key === "enrollment" ? cols.indexOf("convertit") : -1;
      const at = old >= 0 ? old : cols.indexOf(after) + 1;
      cols = [...cols.slice(0, at), key, ...cols.slice(at)];
      localStorage.setItem(marker, "1");
    }
    return cols.filter((c) => c !== "convertit");
  } catch { /* ignore */ }
  return null;
}
function saveColumns(email, cols) {
  try { localStorage.setItem(lsKey(email), JSON.stringify(cols)); } catch { /* ignore */ }
}

export default function LeadsTab({ canMutate, isOwner, currentEmail, currentRole, onLeadConverted }) {
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  // M916 — Pagination state. Page is the single source of truth; the
  // `?page=N` URL query keeps it in sync so refresh/back-button works.
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Math.max(1, parseInt(searchParams.get("page") || "1", 10) || 1);
  const setPage = useCallback((next) => {
    const nextSp = new URLSearchParams(searchParams);
    if (next <= 1) nextSp.delete("page");
    else nextSp.set("page", String(next));
    setSearchParams(nextSp, { replace: false });
  }, [searchParams, setSearchParams]);
  const [pageMeta, setPageMeta] = useState({
    page: 1, page_size: DEFAULT_PAGE_SIZE, total_pages: 1, has_next: false, has_prev: false,
  });
  const [columns, setColumns] = useState(
    () => loadColumns(currentEmail) || ALL_COLUMNS.filter((c) => c.default).map((c) => c.key)
  );
  const [pickerOpen, setPickerOpen] = useState(false);
  const [filters, setFilters] = useState({
    q: "", status: "", source: "", friendly_source: "",
    utm_campaign: "", date: "",
    email: "", date_from: "", date_to: "",
    county: "", specializare: "",
    consent_marketing: "", // "" | "yes" | "no"
    include_deleted: false, test_only: false, mine_only: false,
    enrollment: "", // "" | none | started | converted | phone_only
    due: false, // the next call is due today or overdue
  });
  const [enrollmentCounts, setEnrollmentCounts] = useState(null);
  // Debounced search input — keep `q` typing snappy without thrashing the API.
  const [qDraft, setQDraft] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setFilters((f) => ({ ...f, q: qDraft })), 300);
    return () => clearTimeout(t);
  }, [qDraft]);
  const [activeChip, setActiveChip] = useState("all");
  const [drawerLeadId, setDrawerLeadId] = useState(null);
  // `?lead=<id>` opens that lead (19.10.2026): the "Convertit în lead" link
  // on a webinar registration. Taken off the address once read, so closing
  // the drawer stays closed — the way `?e=` works on the enrolments.
  const linkedLeadId = searchParams.get(LEAD_PARAM);
  useEffect(() => {
    if (!linkedLeadId) return;
    setDrawerLeadId(linkedLeadId);
    const next = new URLSearchParams(searchParams);
    next.delete(LEAD_PARAM);
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedLeadId]);
  const [selected, setSelected] = useState(() => new Set());
  // Bulk changes are the team's (19.10.2026): the server takes them only from
  // owner and Admin FPED, so a partner gets no checkboxes and no bulk bar.
  const canBulk = canMutate && isTeam(currentRole);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // {type, ids, reason, hasPaidLive}

  // Build the query-string shared by list + export so both stay in sync.
  const buildQuery = useCallback(() => {
    const qs = new URLSearchParams();
    if (filters.q) qs.set("q", filters.q);
    if (filters.email) qs.set("email", filters.email);
    if (filters.status) qs.set("status", filters.status);
    if (filters.source) qs.set("source", filters.source);
    if (filters.friendly_source) qs.set("friendly_source", filters.friendly_source);
    if (filters.utm_campaign) qs.set("utm_campaign", filters.utm_campaign);
    if (filters.county) qs.set("county", filters.county);
    if (filters.specializare) qs.set("specializare", filters.specializare);
    if (filters.consent_marketing === "yes") qs.set("consent_marketing", "true");
    if (filters.consent_marketing === "no") qs.set("consent_marketing", "false");
    const fromIso = dateRangeToISO(filters.date);
    if (fromIso) qs.set("date_from", fromIso);
    if (filters.date_from) qs.set("date_from", filters.date_from);
    if (filters.date_to) qs.set("date_to", filters.date_to);
    if (filters.include_deleted) qs.set("include_deleted", "true");
    if (filters.test_only) qs.set("test_only", "true");
    if (filters.mine_only) qs.set("mine_only", "true");
    if (filters.enrollment) qs.set("enrollment", filters.enrollment);
    if (filters.due) qs.set("due", "today");
    return qs;
  }, [filters]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = buildQuery();
      qs.set("page", String(page));
      qs.set("page_size", String(DEFAULT_PAGE_SIZE));
      const r = await api.get(`/admin/leads?${qs.toString()}`);
      setLeads(r.data.items || []);
      setTotal(r.data.total || 0);
      setPageMeta({
        page: r.data.page ?? 1,
        page_size: r.data.page_size ?? DEFAULT_PAGE_SIZE,
        total_pages: r.data.total_pages ?? 1,
        has_next: !!r.data.has_next,
        has_prev: !!r.data.has_prev,
      });
      setSelected(new Set());
      api.get("/admin/leads/enrollment-counts").then((c) => setEnrollmentCounts(c.data)).catch(() => {});
    } catch {
      toast.error("Nu am putut încărca leadurile.");
    } finally {
      setLoading(false);
    }
  }, [buildQuery, page]);

  const exportCsv = (format) => downloadBlob(exportPath("/admin/leads/export", buildQuery(), format),
    exportName("leads", format), null, "Export pornit.");

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [load]);

  // M916 — When filters change (buildQuery identity flips), reset page to 1.
  // We compare a stringified filter signature against a ref so the effect
  // only fires on actual filter mutations, not on every load() re-creation.
  // Skip the initial mount so a deep-link like ?page=3 survives refresh.
  const lastFilterSigRef = useRef(null);
  const filterSig = useMemo(() => buildQuery().toString(), [buildQuery]);
  useEffect(() => {
    if (lastFilterSigRef.current === null) {
      lastFilterSigRef.current = filterSig;
      return;
    }
    if (lastFilterSigRef.current !== filterSig) {
      lastFilterSigRef.current = filterSig;
      setPage(1);
    }
  }, [filterSig, setPage]);

  useEffect(() => { saveColumns(currentEmail, columns); }, [columns, currentEmail]);

  const visibleColumns = useMemo(
    () => ALL_COLUMNS.filter((c) => columns.includes(c.key)),
    [columns],
  );

  const applyChip = (chip) => {
    setActiveChip(chip.id);
    setFilters((f) => ({ ...f, ...chip.filter }));
  };

  const updateStatus = async (lead, status) => {
    try {
      const r = await api.patch(`/admin/leads/${lead.id}`, { status });
      setLeads((prev) => prev.map((l) => (l.id === lead.id ? { ...r.data, enrollment_match: l.enrollment_match } : l)));
      toast.success("Status actualizat");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Eroare");
    }
  };

  const resend = async (e, leadId) => {
    e.stopPropagation();
    try {
      await api.post(`/admin/leads/${leadId}/resend-welcome`);
      toast.success("Am retrimis emailul.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Eroare");
    }
  };

  const toggleRow = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    if (selected.size === leads.length) setSelected(new Set());
    else setSelected(new Set(leads.map((l) => l.id)));
  };

  const bulkApply = async ({ status, assigned_to }) => {
    try {
      const r = await api.post("/admin/leads/bulk", {
        ids: Array.from(selected),
        status: status || null,
        assigned_to: assigned_to !== undefined ? assigned_to : null,
      });
      toast.success(`${r.data.updated} leaduri actualizate`);
      setSelected(new Set());
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Eroare");
    }
  };

  // ---- Soft-delete (owner only) ----
  const requestDelete = (ids) => {
    if (!isOwner || ids.length === 0) return;
    setDeleteConfirm({ ids, reason: "", confirm_paid_live: false, retry409: false });
  };

  const doDelete = async () => {
    if (!deleteConfirm) return;
    const { ids, reason, confirm_paid_live } = deleteConfirm;
    try {
      if (ids.length === 1) {
        await api.delete(`/admin/leads/${ids[0]}`, { data: { reason, confirm_paid_live } });
      } else {
        await api.post("/admin/leads/bulk-delete", { ids, reason, confirm_paid_live });
      }
      toast.success(`${ids.length} lead${ids.length === 1 ? "" : "uri"} șters${ids.length === 1 ? "" : "e"}`);
      setDeleteConfirm(null);
      setSelected(new Set());
      load();
    } catch (err) {
      if (err?.response?.status === 409) {
        // Lead has a paid live purchase — re-prompt for explicit confirm.
        setDeleteConfirm((d) => ({ ...d, retry409: true }));
        toast.message(err?.response?.data?.detail || "Confirmare necesară pentru paid live");
        return;
      }
      toast.error(err?.response?.data?.detail || "Eroare la ștergere");
    }
  };

  const restoreLead = async (l) => {
    try {
      await api.post(`/admin/leads/${l.id}/restore`);
      toast.success("Restaurat");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Eroare");
    }
  };

  return (
    <div data-testid="leads-tab">
      {/* Quick chips */}
      <div className="flex flex-wrap gap-1.5 mb-3" data-testid="leads-chips">
        {CHIPS.map((c) => (
          <button
            key={c.id}
            onClick={() => applyChip(c)}
            data-testid={`leads-chip-${c.id}`}
            className={`rounded-full px-3 py-1.5 text-xs font-semibold border transition-colors ${
              activeChip === c.id
                ? "bg-[#373A45] text-white border-[#373A45]"
                : "bg-white text-[#373A45] border-[#E5E7EB] hover:bg-[#EDEEF0]"
            }`}
          >
            {c.label}
          </button>
        ))}
      </div>

      {/* The call list: where each lead's person stands among enrollments. */}
      <div className="flex flex-wrap items-center gap-1.5 mb-3" data-testid="leads-enrollment-filters">
        <button type="button" aria-pressed={filters.due}
          onClick={() => setFilters((x) => ({ ...x, due: !x.due }))}
          title="Leaduri cu apelul programat azi sau rămas din zilele trecute"
          data-testid="leads-due-today"
          className={`mr-2 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold border transition-colors ${
            filters.due ? "bg-[#373A45] text-white border-[#373A45]" : "bg-white text-[#373A45] border-[#E5E7EB] hover:bg-[#EDEEF0]"}`}>
          De sunat azi
          {typeof enrollmentCounts?.due_today === "number" && (
            <span className={`rounded-full px-1.5 text-[10px] tabular-nums ${filters.due ? "bg-white/25" : "bg-[#FDECEC] text-[#9b1f2c]"}`}>
              {enrollmentCounts.due_today}
            </span>
          )}
        </button>
        <span className="mr-1 text-[11px] font-semibold uppercase tracking-widest text-[#6B7280]">Înscriere</span>
        {ENROLLMENT_FILTERS.map((f) => {
          const on = filters.enrollment === f.id;
          const n = enrollmentCounts?.[f.id];
          return (
            <button key={f.id} type="button" title={f.hint} aria-pressed={on}
              onClick={() => setFilters((x) => ({ ...x, enrollment: on ? "" : f.id }))}
              data-testid={`leads-enrollment-${f.id}`}
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold border transition-colors ${
                on ? "bg-[#965EA7] text-white border-[#965EA7]" : "bg-white text-[#373A45] border-[#E5E7EB] hover:bg-[#F5F0F8]"}`}>
              {f.label}
              {typeof n === "number" && (
                <span className={`rounded-full px-1.5 text-[10px] tabular-nums ${on ? "bg-white/25" : "bg-[#EDEEF0] text-[#6B7280]"}`}>{n}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Quick filters (always visible) + Advanced (collapsible) */}
      <LeadsFilterBar
        filters={filters}
        setFilters={setFilters}
        qDraft={qDraft}
        setQDraft={setQDraft}
        isOwner={isOwner}
        currentRole={currentRole}
        onOpenColumns={() => setPickerOpen(true)}
        onExport={exportCsv}
      />

      <div className="text-xs text-[#6B7280] mb-2">
        {loading ? "Se încarcă…" : `${total} leaduri`}
        {selected.size > 0 && <span className="ml-2 text-[#965EA7] font-semibold">· {selected.size} selectate</span>}
      </div>

      <div className="bg-white rounded-2xl border border-[#E5E7EB] overflow-x-auto">
        <table className="w-full text-sm" data-testid="leads-table">
          <thead className="bg-[#EDEEF0] text-[#373A45]">
            <tr className="text-left">
              {canBulk && (
                <th className="px-3 py-3 w-8">
                  <button
                    onClick={toggleAll}
                    className="text-[#6B7280] hover:text-[#373A45]"
                    aria-label="Selectează tot"
                    data-testid="leads-select-all"
                  >
                    {leads.length > 0 && selected.size === leads.length
                      ? <CheckSquare size={14} className="text-[#965EA7]" />
                      : <Square size={14} />}
                  </button>
                </th>
              )}
              {visibleColumns.map((c) => (
                <th key={c.key} className="px-4 py-3 text-xs uppercase tracking-widest font-semibold whitespace-nowrap">{c.label}</th>
              ))}
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {leads.length === 0 && !loading && (
              <tr><td colSpan={visibleColumns.length + (canBulk ? 2 : 1)} className="p-6 text-center text-[#6B7280]">Niciun lead găsit cu filtrele curente.</td></tr>
            )}
            {leads.map((l) => (
              <tr
                key={l.id}
                onClick={() => setDrawerLeadId(l.id)}
                className={`border-t border-[#E5E7EB] hover:bg-[#F5F6F6] cursor-pointer ${selected.has(l.id) ? "bg-[#F5F0F8]" : ""}`}
                data-testid={`leads-row-${l.id}`}
              >
                {canBulk && (
                  <td className="px-3 py-3 w-8" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => toggleRow(l.id)}
                      data-testid={`leads-checkbox-${l.id}`}
                      className="text-[#6B7280] hover:text-[#373A45]"
                      aria-label={selected.has(l.id) ? "Deselectează" : "Selectează"}
                    >
                      {selected.has(l.id)
                        ? <CheckSquare size={14} className="text-[#965EA7]" />
                        : <Square size={14} />}
                    </button>
                  </td>
                )}
                {visibleColumns.map((c) => (
                  <td key={c.key} className="px-4 py-3 text-[#373A45] align-top whitespace-nowrap">
                    <Cell lead={l} colKey={c.key} canMutate={canMutate} onStatus={updateStatus}
                      onCallLogged={(u) => {
                        setLeads((prev) => prev.map((x) => (x.id === u.id ? { ...x, ...u } : x)));
                        api.get("/admin/leads/enrollment-counts").then((c2) => setEnrollmentCounts(c2.data)).catch(() => {});
                      }} />
                  </td>
                ))}
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex items-center gap-3">
                    {l.deleted_at && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-[#6B7280]/15 text-[#373A45] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest" title={`Șters la ${new Date(l.deleted_at).toLocaleString("ro-RO")} de ${l.deleted_by || "?"}`}>
                        Șters
                      </span>
                    )}
                    {canMutate && canResendWelcome(l) && (
                      <button
                        onClick={(e) => resend(e, l.id)}
                        className="inline-flex items-center gap-1 text-[#965EA7] hover:underline text-xs"
                        data-testid={`resend-${l.id}`}
                        title={welcomeTitle(l)}
                      >
                        <Send size={12} />
                      </button>
                    )}
                    {isOwner && l.deleted_at && (
                      <button
                        onClick={(e) => { e.stopPropagation(); restoreLead(l); }}
                        className="inline-flex items-center gap-1 text-[#519872] hover:underline text-xs"
                        data-testid={`leads-restore-${l.id}`}
                        title="Restaurează lead"
                      >
                        <RotateCcw size={12} />
                      </button>
                    )}
                    {isOwner && !l.deleted_at && (
                      <button
                        onClick={(e) => { e.stopPropagation(); requestDelete([l.id]); }}
                        className="inline-flex items-center gap-1 text-[#9b1f2c] hover:underline text-xs"
                        data-testid={`leads-delete-${l.id}`}
                        title="Șterge lead"
                      >
                        <Trash2 size={12} />
                      </button>
                    )}
                    <ChevronRight size={14} className="text-[#6B7280]" />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pagination
        page={pageMeta.page}
        pageSize={pageMeta.page_size}
        total={total}
        totalPages={pageMeta.total_pages}
        hasNext={pageMeta.has_next}
        hasPrev={pageMeta.has_prev}
        onPageChange={setPage}
        testid="leads-pagination"
      />

      {/* Bulk actions floating bar */}
      {selected.size > 0 && canBulk && (
        <BulkBar
          count={selected.size}
          onClear={() => setSelected(new Set())}
          onApply={bulkApply}
          onDelete={isOwner ? () => requestDelete(Array.from(selected)) : null}
        />
      )}

      {deleteConfirm && (
        <DeleteConfirmModal
          state={deleteConfirm}
          onChange={setDeleteConfirm}
          onCancel={() => setDeleteConfirm(null)}
          onConfirm={doDelete}
        />
      )}

      {pickerOpen && (
        <ColumnPickerModal
          columns={columns}
          onClose={() => setPickerOpen(false)}
          onSave={(next) => { setColumns(next); setPickerOpen(false); }}
        />
      )}

      {drawerLeadId && (
        <LeadDrawer
          leadId={drawerLeadId}
          canMutate={canMutate}
          currentRole={currentRole}
          onClose={() => setDrawerLeadId(null)}
          onUpdated={(updated) => {
            setLeads((prev) => prev.map((l) => (l.id === updated.id ? { ...l, ...updated } : l)));
          }}
          onOpenEnrollment={(enrollmentId) => {
            // Same jump as after a conversion: Înscrieri, with that drawer open.
            setDrawerLeadId(null);
            onLeadConverted?.(enrollmentId);
          }}
          onConverted={(enrollmentId) => {
            // Lead just became an enrollment — refresh list, close drawer and
            // bubble up so the parent (AdminDashboard) can jump to Enrollments.
            setDrawerLeadId(null);
            load();
            onLeadConverted?.(enrollmentId);
          }}
        />
      )}
    </div>
  );
}

function BulkBar({ count, onClear, onApply, onDelete }) {
  const [status, setStatus] = useState("");
  const [assignTo, setAssignTo] = useState("");

  const apply = () => {
    if (!status && !assignTo) return;
    onApply({
      status: status || undefined,
      assigned_to: assignTo !== "" ? assignTo : undefined,
    });
    setStatus(""); setAssignTo("");
  };

  return (
    <div
      className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 bg-[#373A45] text-white rounded-full px-3 py-2 shadow-2xl flex items-center gap-2 flex-wrap"
      data-testid="leads-bulk-bar"
    >
      <span className="text-xs font-semibold pl-3">{count} selectate</span>
      <select
        value={status}
        onChange={(e) => setStatus(e.target.value)}
        className="rounded-full px-3 py-1.5 text-xs text-[#373A45]"
        data-testid="leads-bulk-status"
      >
        <option value="">Schimbă status…</option>
        {STATUSES.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
      </select>
      <input
        value={assignTo}
        onChange={(e) => setAssignTo(e.target.value)}
        placeholder="Atribuie email"
        className="rounded-full px-3 py-1.5 text-xs text-[#373A45] bg-white border-0 outline-none w-44"
        data-testid="leads-bulk-assign"
      />
      <button
        onClick={apply}
        disabled={!status && !assignTo}
        className="rounded-full bg-[#965EA7] hover:bg-[#7A4986] disabled:opacity-40 px-4 py-1.5 text-xs font-semibold"
        data-testid="leads-bulk-apply"
      >
        Aplică
      </button>
      {onDelete && (
        <button
          onClick={onDelete}
          className="rounded-full bg-[#9b1f2c] hover:bg-[#7e1923] px-4 py-1.5 text-xs font-semibold inline-flex items-center gap-1"
          data-testid="leads-bulk-delete"
        >
          <Trash2 size={12} /> Șterge
        </button>
      )}
      <button
        onClick={onClear}
        className="rounded-full px-3 py-1.5 text-xs text-white/80 hover:text-white"
        data-testid="leads-bulk-clear"
        aria-label="Anulează selecția"
      >
        <X size={14} />
      </button>
    </div>
  );
}

function DeleteConfirmModal({ state, onChange, onCancel, onConfirm }) {
  const isBulk = state.ids.length > 1;
  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-black/40 backdrop-blur-sm p-4"
      onClick={onCancel}
      data-testid="leads-delete-confirm"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-3xl border border-[#E5E7EB] shadow-2xl w-full max-w-md"
      >
        <div className="flex items-center justify-between p-5 border-b border-[#E5E7EB]">
          <h3 className="font-display text-lg font-bold text-[#9b1f2c] inline-flex items-center gap-2">
            <Trash2 size={18} /> Ștergere {isBulk ? `${state.ids.length} leaduri` : "lead"}
          </h3>
          <button onClick={onCancel} className="text-[#6B7280] hover:text-[#373A45]" aria-label="Închide">
            <X size={18} />
          </button>
        </div>
        <div className="p-5 space-y-3 text-sm text-[#373A45]">
          <p>
            Ștergerea este <strong>soft</strong> — rândul rămâne în DB cu <code className="bg-[#EDEEF0] px-1 rounded text-xs">deleted_at</code>
            și poate fi restaurat. Se scrie și un entry în Audit.
          </p>
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-widest text-[#373A45]">Motiv (opțional)</span>
            <input
              type="text"
              value={state.reason}
              onChange={(e) => onChange({ ...state, reason: e.target.value })}
              placeholder="ex. lead QA / test"
              className="mt-1 w-full bg-[#F5F6F6] border border-[#E5E7EB] rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#965EA7]"
              data-testid="leads-delete-reason"
            />
          </label>
          {state.retry409 && (
            <div className="rounded-xl bg-[#FDF3E7] border border-[#F59E0B]/40 p-3 text-xs text-[#8a6a1a]">
              <strong>Atenție:</strong> {isBulk ? "Unul sau mai multe leaduri au" : "Acest lead are"} o comandă <strong>paid LIVE</strong>.
              Bifează caseta de mai jos ca să continui.
            </div>
          )}
          <label className="flex items-center gap-2 text-xs text-[#373A45]">
            <input
              type="checkbox"
              checked={state.confirm_paid_live}
              onChange={(e) => onChange({ ...state, confirm_paid_live: e.target.checked })}
              className="accent-[#9b1f2c]"
              data-testid="leads-delete-confirm-paid"
            />
            Confirm că pot șterge inclusiv leaduri cu comenzi paid live
          </label>
        </div>
        <div className="flex items-center justify-end gap-2 p-5 border-t border-[#E5E7EB] bg-[#F5F6F6]/60">
          <button onClick={onCancel} className="rounded-full px-4 py-2 text-sm border border-[#E5E7EB] hover:bg-white">
            Anulează
          </button>
          <button
            onClick={onConfirm}
            className="inline-flex items-center gap-1.5 rounded-full bg-[#9b1f2c] hover:bg-[#7e1923] text-white px-5 py-2 text-sm font-semibold"
            data-testid="leads-delete-go"
          >
            <Trash2 size={14} /> Șterge {isBulk ? state.ids.length : ""}
          </button>
        </div>
      </div>
    </div>
  );
}

function CallCell({ lead, canMutate, onCallLogged }) {
  const [open, setOpen] = useState(false);
  const last = lead.last_call;
  const o = last && (OUTCOME_BY_ID[last.outcome] || { label: last.outcome, cls: "bg-[#EDEEF0] text-[#373A45]" });
  const next = lead.follow_up_date;
  const overdue = next && next < todayRo();
  return (
    <span className="flex flex-col gap-0.5 leading-tight" onClick={(e) => e.stopPropagation()}
      data-testid={`leads-call-cell-${lead.id}`}>
      {o ? (
        <span className="flex items-center gap-1.5">
          <span className={`inline-flex w-fit rounded-full px-2 py-0.5 text-[11px] font-semibold ${o.cls}`}>{o.label}</span>
          <span className="text-[11px] text-[#6B7280]">{new Date(last.at).toLocaleDateString("ro-RO")}</span>
        </span>
      ) : <span className="text-[11px] text-[#6B7280]">Nesunat</span>}
      {next && (
        <span className={`text-[11px] ${overdue ? "font-semibold text-[#9b1f2c]" : "text-[#6B7280]"}`}>
          următorul: {nextCallLabel(next)}
        </span>
      )}
      {canMutate && (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button type="button" className="w-fit text-[11px] font-semibold text-[#965EA7] hover:underline"
              data-testid={`leads-call-log-${lead.id}`}>
              + Înregistrează apel
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="z-[60] w-[340px] p-3" onOpenAutoFocus={(e) => e.preventDefault()}>
            <div className="mb-2 text-xs font-semibold text-[#373A45]">
              {lead.prenume} {lead.nume} · {lead.telefon || "fără telefon"}
            </div>
            <LeadCallPanel lead={lead} compact onLogged={(u) => { setOpen(false); onCallLogged?.(u); }} />
          </PopoverContent>
        </Popover>
      )}
    </span>
  );
}

function Cell({ lead, colKey, canMutate, onStatus, onCallLogged }) {
  const utm = lead.utm || {};
  switch (colKey) {
    case "nume": return <span className="font-medium">{lead.nume}</span>;
    case "prenume": return <span>{lead.prenume}</span>;
    case "email":
      return (
        <span className="inline-flex items-center gap-1.5 text-[#373A45]">
          {lead.email}
          {lead.possible_duplicate && (
            <span
              className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold bg-[#F59E0B]/20 text-[#92400E]"
              title="Acest email există deja în CRM"
              data-testid={`leads-duplicate-badge-${lead.id}`}
            >
              <AlertTriangle size={10} /> posibil duplicat
            </span>
          )}
        </span>
      );
    case "telefon": return <span>{lead.telefon || "—"}</span>;
    case "specializare": return <span>{lead.specializare || "—"}</span>;
    // Absent on leads from before the contact form existed — those all came
    // through the brochure form, but saying so would be a guess, so: a dash.
    case "form_variant": return lead.form_variant
      ? <span className="inline-flex items-center rounded-full bg-[#EDEEF0] text-[#373A45] px-2 py-0.5 text-[11px] font-semibold">
          {lead.form_variant === "brochure" ? "Broșură" : "Contact"}
        </span>
      : <span className="text-[#6B7280]">—</span>;
    case "unitate": return <span>{lead.unitate || "—"}</span>;
    case "city": return <span>{lead.city || "—"}</span>;
    case "county": return lead.county
      ? <span className="inline-flex items-center rounded-full bg-[#EDEEF0] text-[#373A45] px-2 py-0.5 text-[11px] font-semibold">{lead.county}</span>
      : <span className="text-[#6B7280]">—</span>;
    case "last_call":
      return <CallCell lead={lead} canMutate={canMutate} onCallLogged={onCallLogged} />;
    case "enrollment": {
      const badge = matchBadge(lead.enrollment_match);
      if (!badge) return <span className="text-[#6B7280]">—</span>;
      const ticks = matchTicks(lead.enrollment_match);
      const when = lead.enrollment_match.created_at
        ? new Date(lead.enrollment_match.created_at).toLocaleDateString("ro-RO") : "";
      return (
        <span className="flex flex-col gap-0.5 leading-tight" data-testid={`leads-enrollment-cell-${lead.id}`}>
          <span className={`inline-flex w-fit items-center whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold ${badge.cls}`}>
            {badge.label}
          </span>
          {(badge.detail || when) && (
            <span className="text-[11px] text-[#6B7280]">{[badge.detail, when].filter(Boolean).join(" · ")}</span>
          )}
          {ticks.length > 0 && <span className="text-[10px] font-semibold text-[#2f6b50]">{ticks.join(" · ")}</span>}
        </span>
      );
    }
    case "status":
      return (
        <select
          value={lead.status || "nou"}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => onStatus(lead, e.target.value)}
          disabled={!canMutate}
          className={`rounded-full px-2.5 py-1 text-[11px] font-semibold border-0 ${(STATUS_BY_VALUE[lead.status]?.cls) || "bg-[#EDEEF0]"} disabled:opacity-50`}
          data-testid={`leads-status-${lead.id}`}
        >
          {STATUSES.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
        </select>
      );
    case "source": return <span className="text-xs">{leadSourceLabel(lead.source || "homepage_form")}</span>;
    case "friendly_source": {
      const fs = lead.friendly_source || "Unknown";
      const cls = FRIENDLY_SOURCE_TONE[fs] || "bg-[#6B7280]/10 text-[#6B7280]";
      return (
        <span
          className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${cls}`}
          data-testid={`leads-channel-${lead.id}`}
        >
          {fs}
        </span>
      );
    }
    case "popup_trigger_type": return <span className="text-xs">{lead.popup_trigger_type || "—"}</span>;
    case "popup_cta_clicked": return <span className="text-xs">{lead.popup_cta_clicked ? "✓" : "—"}</span>;
    case "utm_source": return <span className="text-xs">{utm.utm_source || "—"}</span>;
    case "utm_medium": return <span className="text-xs">{utm.utm_medium || "—"}</span>;
    case "utm_campaign": return <span className="text-xs">{utm.utm_campaign || "—"}</span>;
    case "utm_content": return <span className="text-xs">{utm.utm_content || "—"}</span>;
    case "utm_term": return <span className="text-xs">{utm.utm_term || "—"}</span>;
    case "consent_privacy": return <span>{lead.consent_privacy ? "✓" : "—"}</span>;
    case "assigned_to_email":
      return lead.assigned_to_email
        ? <span className="inline-flex items-center rounded-full bg-[#519872]/15 text-[#2F6B4E] px-2 py-0.5 text-[11px] font-medium" title={lead.assigned_to_email} data-testid={`leads-assigned-${lead.id}`}>{lead.assigned_to_email.split("@")[0]}</span>
        : <span className="text-xs text-[#6B7280] italic" data-testid={`leads-unassigned-${lead.id}`}>Neatribuit</span>;
    case "consent_marketing":
      return lead.consent_marketing
        ? <span className="inline-flex items-center rounded-full bg-[#519872]/18 text-[#2F6B4E] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest" data-testid={`leads-mkt-yes-${lead.id}`}>Da</span>
        : <span className="inline-flex items-center rounded-full bg-[#E63946]/15 text-[#9b1f2c] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest" data-testid={`leads-mkt-no-${lead.id}`}>Nu</span>;
    case "created_at": return <span className="text-xs text-[#6B7280]">{new Date(lead.created_at).toLocaleString("ro-RO")}</span>;
    case "updated_at": return <span className="text-xs text-[#6B7280]">{lead.updated_at ? new Date(lead.updated_at).toLocaleString("ro-RO") : "—"}</span>;
    case "notes_count": return <span className="inline-flex items-center gap-1 text-xs"><FileText size={11} />{lead.notes_count || 0}</span>;
    default: return null;
  }
}

function ColumnPickerModal({ columns, onClose, onSave }) {
  const [draft, setDraft] = useState(columns);
  const toggle = (key) =>
    setDraft((d) => (d.includes(key) ? d.filter((k) => k !== key) : [...d, key]));
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 backdrop-blur-sm p-4" onClick={onClose} data-testid="leads-column-picker">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-3xl border border-[#E5E7EB] shadow-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-[#E5E7EB]">
          <h3 className="font-display text-lg font-bold text-[#373A45]">Alege coloane</h3>
          <button onClick={onClose} className="text-[#6B7280] hover:text-[#373A45]" aria-label="Închide"><X size={18} /></button>
        </div>
        <div className="p-5 grid grid-cols-2 gap-2">
          {ALL_COLUMNS.map((c) => {
            const on = draft.includes(c.key);
            return (
              <label key={c.key} className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm cursor-pointer transition-colors ${on ? "bg-[#F5F0F8] border-[#965EA7]/40 text-[#373A45]" : "bg-white border-[#E5E7EB] text-[#6B7280] hover:border-[#965EA7]/40"}`}>
                <input
                  type="checkbox"
                  checked={on}
                  onChange={() => toggle(c.key)}
                  data-testid={`leads-col-${c.key}`}
                  className="accent-[#965EA7]"
                />
                {c.label}
              </label>
            );
          })}
        </div>
        <div className="flex items-center justify-end gap-2 p-5 border-t border-[#E5E7EB] bg-[#F5F6F6]/60">
          <button onClick={onClose} className="rounded-full px-4 py-2 text-sm border border-[#E5E7EB] hover:bg-white">Anulează</button>
          <button
            onClick={() => onSave(draft)}
            className="rounded-full bg-[#965EA7] hover:bg-[#7A4986] text-white px-5 py-2 text-sm font-semibold"
            data-testid="leads-col-save"
          >
            Salvează
          </button>
        </div>
      </div>
    </div>
  );
}

const ADV_LS_KEY = "fped_leads_adv_open";

function LeadsFilterBar({ filters, setFilters, qDraft, setQDraft, isOwner, currentRole, onOpenColumns, onExport }) {
  const [open, setOpen] = useState(() => {
    try { return localStorage.getItem(ADV_LS_KEY) === "1"; } catch { return false; }
  });
  const toggle = () => setOpen((v) => {
    const next = !v;
    try { localStorage.setItem(ADV_LS_KEY, next ? "1" : "0"); } catch { /* ignore */ }
    return next;
  });

  // Advanced filter active count — surfaces a "•" badge so users notice the
  // filters tucked behind the disclosure.
  const advCount = [
    filters.specializare, filters.email, filters.utm_campaign,
    filters.consent_marketing, filters.date_from, filters.date_to,
    filters.source, filters.test_only ? "1" : "", filters.include_deleted ? "1" : "",
  ].filter(Boolean).length;

  return (
    <div className="bg-white border border-[#E5E7EB] rounded-xl mb-3" data-testid="leads-filter-bar">
      {/* Quick filters */}
      <div className="flex flex-wrap items-center gap-2 p-2.5">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={14} className="absolute top-2.5 left-3 text-[#6B7280]" />
          <input
            value={qDraft}
            onChange={(e) => setQDraft(e.target.value)}
            placeholder="Caută în nume, email, telefon, unitate, oraș, județ…"
            className="w-full bg-[#F5F6F6] border border-[#E5E7EB] rounded-full pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-[#965EA7]"
            data-testid="leads-search"
            autoComplete="off"
            spellCheck={false}
          />
        </div>
        <select
          value={filters.status}
          onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          className="rounded-full px-3 py-2 text-sm border border-[#E5E7EB] bg-white"
          data-testid="leads-filter-status"
        >
          <option value="">Toate statusurile</option>
          {STATUSES.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
        </select>
        <div className="w-40">
          <CountyCombobox
            options={ROMANIAN_COUNTIES}
            value={filters.county}
            onChange={(v) => setFilters({ ...filters, county: v })}
            placeholder="Toate județele"
            testId="leads-filter-county"
          />
        </div>
        <select
          value={filters.friendly_source}
          onChange={(e) => setFilters({ ...filters, friendly_source: e.target.value })}
          className="rounded-full px-3 py-2 text-sm border border-[#E5E7EB] bg-white"
          data-testid="leads-filter-channel"
        >
          <option value="">Toate canalele</option>
          {FRIENDLY_SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <button
          onClick={toggle}
          className={`text-sm rounded-full px-3 py-2 border inline-flex items-center gap-1.5 transition-colors ${open ? "border-[#965EA7]/40 bg-[#F5F0F8] text-[#7A4986]" : "border-[#E5E7EB] text-[#373A45] hover:bg-[#F5F6F6]"}`}
          data-testid="leads-advanced-toggle"
          aria-expanded={open}
        >
          <SlidersHorizontal size={14} /> Avansate
          {advCount > 0 && (
            <span className="rounded-full bg-[#965EA7] text-white text-[10px] font-bold px-1.5 leading-none py-0.5">{advCount}</span>
          )}
          <ChevronDown size={12} className={`transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={onOpenColumns}
            className="rounded-full px-3 py-2 text-sm border border-[#E5E7EB] hover:bg-[#F5F6F6] inline-flex items-center gap-1.5"
            data-testid="leads-column-picker-button"
            title="Coloane vizibile"
          >
            <Settings2 size={14} /> Coloane
          </button>
          <ExportButton
            onExport={onExport}
            className="rounded-full px-3 py-2 text-sm bg-[#373A45] text-white hover:bg-[#1F2128] inline-flex items-center gap-1.5"
            testid="leads-export-csv"
            title="Exportă rezultatele filtrate curent"
          />
        </div>
      </div>

      {/* Advanced filters */}
      {open && (
        <div className="border-t border-[#E5E7EB] p-2.5 flex flex-wrap items-center gap-2 animate-in fade-in slide-in-from-top-1 duration-150" data-testid="leads-advanced-panel">
          <input
            value={filters.specializare}
            onChange={(e) => setFilters({ ...filters, specializare: e.target.value })}
            placeholder="Specializare"
            className="rounded-full px-3 py-2 text-sm border border-[#E5E7EB] bg-white w-44"
            data-testid="leads-filter-specializare"
            autoComplete="off"
          />
          <input
            value={filters.email}
            onChange={(e) => setFilters({ ...filters, email: e.target.value })}
            placeholder="Email exact"
            className="rounded-full px-3 py-2 text-sm border border-[#E5E7EB] bg-white w-52"
            data-testid="leads-filter-email"
            autoComplete="off"
          />
          <select
            value={filters.source}
            onChange={(e) => setFilters({ ...filters, source: e.target.value })}
            className="rounded-full px-3 py-2 text-sm border border-[#E5E7EB] bg-white"
            data-testid="leads-filter-source"
          >
            <option value="">Toate sursele</option>
            {SOURCES.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
          </select>
          <select
            value={filters.consent_marketing}
            onChange={(e) => setFilters({ ...filters, consent_marketing: e.target.value })}
            className="rounded-full px-3 py-2 text-sm border border-[#E5E7EB] bg-white"
            data-testid="leads-filter-consent"
          >
            <option value="">Consent: oricare</option>
            <option value="yes">Cu consent marketing</option>
            <option value="no">Fără consent</option>
          </select>
          <input
            value={filters.utm_campaign}
            onChange={(e) => setFilters({ ...filters, utm_campaign: e.target.value })}
            placeholder="UTM campaign"
            className="rounded-full px-3 py-2 text-sm border border-[#E5E7EB] bg-white w-44"
            data-testid="leads-filter-utm"
            autoComplete="off"
          />
          <label className="text-xs inline-flex items-center gap-1.5 text-[#373A45]">
            De la
            <input
              type="date"
              value={filters.date_from?.slice(0, 10) || ""}
              onChange={(e) => setFilters({ ...filters, date_from: e.target.value ? `${e.target.value}T00:00:00Z` : "" })}
              className="rounded-full px-3 py-1 text-xs border border-[#E5E7EB] bg-white"
              data-testid="leads-filter-date-from"
            />
          </label>
          <label className="text-xs inline-flex items-center gap-1.5 text-[#373A45]">
            Până la
            <input
              type="date"
              value={filters.date_to?.slice(0, 10) || ""}
              onChange={(e) => setFilters({ ...filters, date_to: e.target.value ? `${e.target.value}T23:59:59Z` : "" })}
              className="rounded-full px-3 py-1 text-xs border border-[#E5E7EB] bg-white"
              data-testid="leads-filter-date-to"
            />
          </label>
          {isOwner && (
            <>
              <label className="text-xs inline-flex items-center gap-1.5 text-[#373A45] cursor-pointer" data-testid="leads-filter-testonly-label">
                <input
                  type="checkbox"
                  checked={filters.test_only}
                  onChange={(e) => setFilters({ ...filters, test_only: e.target.checked })}
                  className="accent-[#9b1f2c]"
                  data-testid="leads-filter-testonly"
                />
                Doar test
              </label>
              <label className="text-xs inline-flex items-center gap-1.5 text-[#373A45] cursor-pointer" data-testid="leads-filter-deleted-label">
                <input
                  type="checkbox"
                  checked={filters.include_deleted}
                  onChange={(e) => setFilters({ ...filters, include_deleted: e.target.checked })}
                  className="accent-[#965EA7]"
                  data-testid="leads-filter-deleted"
                />
                Arată șterse
              </label>
            </>
          )}
          {(currentRole === "owner" || currentRole === "admin_fped" || currentRole === "admin") && (
            <label
              className="text-xs inline-flex items-center gap-1.5 text-[#373A45] cursor-pointer"
              data-testid="leads-filter-mineonly-label"
            >
              <input
                type="checkbox"
                checked={filters.mine_only}
                onChange={(e) => setFilters({ ...filters, mine_only: e.target.checked })}
                className="accent-[#519872]"
                data-testid="leads-filter-mineonly"
              />
              Doar ale mele
            </label>
          )}
        </div>
      )}
    </div>
  );
}

